import type { BeamType } from "src/graphql/generated";

/**
 * Beam is one NPD subcategory, and its two extra fields are shown for that one only.
 *
 * Which subcategory that is comes from the `code` the lookups query already carries,
 * compared against this constant — seeded by migration 000007 and matched by the
 * backend's own `subcategoryCodeBeam`. That is a real coupling to a seeded value:
 * rename the code in the database and the section quietly stops appearing. The
 * alternative is a flag column on `need_npd_subcategory`, which puts a second answer
 * to "is this Beam" next to the code and lets the two disagree — not worth it for a
 * ten-row seeded table. It earns its place when a second subcategory grows fields of
 * its own.
 *
 * Stated once, here, so the string is not spelled out at each of the three places
 * that ask the question.
 */
export const BEAM_SUBCATEGORY_CODE = "beam";

export function isBeamSubcategory(
  subcategory: { code?: string | null } | null | undefined
): boolean {
  return subcategory?.code === BEAM_SUBCATEGORY_CODE;
}

/** The pickable beam types, in the order the forms offer them. */
export const BEAM_TYPES: { value: BeamType; label: string }[] = [
  { value: "REGIONAL", label: "Regional" },
  { value: "CRC", label: "CRC" }
];

export function humanBeamType(value: BeamType): string {
  return BEAM_TYPES.find((t) => t.value === value)?.label ?? value;
}

/**
 * One line describing what a Beam's term records, for the engagements table.
 *
 * "Not recorded" is said rather than left blank, because blank is also what a
 * subcategory with no such fields shows: on a row that is a Beam, an empty cell would
 * read as "this has no beam number" rather than "nobody has entered it yet", and the
 * second is a thing to go and do.
 */
export function describeBeamDetail(
  detail: { beamNumber?: string | null; beamType?: BeamType | null } | null | undefined
): string {
  const parts: string[] = [];
  if (detail?.beamNumber) {
    parts.push(`No. ${detail.beamNumber}`);
  }
  if (detail?.beamType) {
    parts.push(humanBeamType(detail.beamType));
  }
  return parts.length > 0 ? parts.join(" · ") : "Beam number and type not recorded";
}
